import Konva from 'konva';
import * as state from '../state.js';
import { attachInteractive, commitInteractive, pointerPos } from './_helpers.js';

// Each preset is applied to a Group(Rect, Text). Background, padding, border,
// and font are driven by the preset; color is driven by selectedColor.
// `bg: 'currentColor'` resolves to a solid pale tint of the selectedColor
// (mixed with white at `bgTint` weight) — opaque so screenshots underneath
// don't bleed through and clash with the text. Lower bgTint = more saturated.
const PRESETS = {
  standard:    { font: 'DM Sans, sans-serif', weight: 500, padding: 0,  bg: null,           border: null, textColor: 'currentColor' },
  mono:        { font: 'DM Mono, monospace',  weight: 500, padding: 0,  bg: null,           border: null, textColor: 'currentColor' },
  box:         { font: 'DM Sans, sans-serif', weight: 600, padding: 8,  bg: 'currentColor', bgTint: 0.88, border: null, textColor: 'currentColor', radius: 6 },
  'mono-box':  { font: 'DM Mono, monospace',  weight: 500, padding: 8,  bg: 'currentColor', bgTint: 0.88, border: null, textColor: 'currentColor', radius: 6 },
  'rounded-box': { font: 'DM Sans, sans-serif', weight: 600, padding: 10, bg: 'currentColor', bgTint: 0.88, border: null, textColor: 'currentColor', radius: 999 },
};

function parseHex(color) {
  if (!color || typeof color !== 'string' || !color.startsWith('#')) return null;
  const hex = color.slice(1);
  const expanded = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  if (expanded.length !== 6) return null;
  return [
    parseInt(expanded.slice(0, 2), 16),
    parseInt(expanded.slice(2, 4), 16),
    parseInt(expanded.slice(4, 6), 16),
  ];
}

// Mix `color` with white at `weight` (0 = pure color, 1 = pure white). Used
// for box-preset bgs so they read as a soft, opaque tint of the text color.
function tintWithWhite(color, weight) {
  const rgb = parseHex(color);
  if (!rgb) return color;
  const w = Math.max(0, Math.min(1, weight));
  const [r, g, b] = rgb.map((c) => Math.round(c + (255 - c) * w));
  return `rgb(${r}, ${g}, ${b})`;
}

function resolveBg(preset, color) {
  if (!preset.bg) return null;
  if (preset.bg === 'currentColor') return tintWithWhite(color, preset.bgTint ?? 0);
  return preset.bg;
}

function buildText(engine, x, y, value, fontSize, presetKey, color) {
  const preset = PRESETS[presetKey] ?? PRESETS.standard;
  const text = new Konva.Text({
    text: value || ' ',
    fontFamily: preset.font,
    fontStyle: String(preset.weight),
    fontSize,
    fill: preset.textColor === 'currentColor' ? color : preset.textColor,
    padding: preset.padding,
  });
  const group = new Konva.Group({ x, y });
  if (preset.bg || preset.border) {
    const bg = new Konva.Rect({
      width: text.width(),
      height: text.height(),
      fill: resolveBg(preset, color) ?? 'transparent',
      stroke: preset.border ? color : null,
      strokeWidth: preset.border?.width ?? 0,
      cornerRadius: preset.radius ?? 0,
    });
    group.add(bg);
    text.on('textChange', () => {
      bg.width(text.width());
      bg.height(text.height());
    });
  }
  group.add(text);
  group.preset = presetKey;
  group.textNode = text;
  return group;
}

export const text = {
  id: 'text',
  defaultProps: { preset: 'box', fontSize: 20 },
  onPointerDown(engine) {
    const pos = pointerPos(engine);
    if (!engine.pointerInsideImage(pos)) return;
    const defaults = state.get('toolDefaults').text ?? this.defaultProps;
    const color = state.get('selectedColor');
    const group = buildText(engine, pos.x, pos.y, '', defaults.fontSize, defaults.preset, color);
    // Defer the undo op until after the user commits non-empty text; an empty
    // commit destroys the placeholder so it never appears in history.
    attachInteractive(engine, group, { deferOp: true });
    // Re-edit on double-click. Wired here (once, on the live node instance)
    // so undo/redo doesn't have to re-attach.
    group.on('dblclick dbltap', (e) => {
      e.cancelBubble = true;
      beginTextEdit(engine, group, { isPlacement: false });
    });
    beginTextEdit(engine, group, { isPlacement: true });
  },
  onPointerMove() {},
  onPointerUp() {},
  apply(node, prop, value) {
    if (prop === 'color') return applyColor(node, value);
    if (prop === 'preset') return applyPreset(node, value);
    if (prop === 'fontSize') return applyFontSize(node, value);
  },
};

function applyColor(node, value) {
  const preset = PRESETS[node.preset] ?? PRESETS.standard;
  if (node.textNode && preset.textColor === 'currentColor') {
    node.textNode.fill(value);
  }
  const bg = node.children?.find?.((c) => c.getClassName?.() === 'Rect');
  if (bg) {
    if (preset.border) bg.stroke(value);
    if (preset.bg === 'currentColor') bg.fill(tintWithWhite(value, preset.bgTint ?? 0));
  }
}

function applyFontSize(node, value) {
  if (!node.textNode) return;
  node.textNode.fontSize(value);
  syncBgToText(node);
}

// Re-skin an existing text node when the user picks a different preset from
// the toolbar. Updates the Text node's font/weight/padding/fill, swaps the bg
// Rect in or out as needed, and re-runs the bg sizing logic.
function applyPreset(node, presetKey) {
  const preset = PRESETS[presetKey] ?? PRESETS.standard;
  const text = node.textNode;
  if (!text) return;
  const color = state.get('selectedColor');

  text.fontFamily(preset.font);
  text.fontStyle(String(preset.weight));
  text.padding(preset.padding ?? 0);
  text.fill(preset.textColor === 'currentColor' ? color : preset.textColor);

  let bg = node.children?.find?.((c) => c.getClassName?.() === 'Rect');
  const wantsRect = !!(preset.bg || preset.border);

  if (wantsRect && !bg) {
    bg = new Konva.Rect({
      width: text.width(),
      height: text.height(),
    });
    // Insert the bg behind the text so the text stays on top.
    node.add(bg);
    bg.moveToBottom();
    text.on('textChange', () => {
      bg.width(text.width());
      bg.height(text.height());
    });
  } else if (!wantsRect && bg) {
    bg.destroy();
    bg = null;
  }

  if (bg) {
    bg.fill(resolveBg(preset, color) ?? 'transparent');
    bg.stroke(preset.border ? color : null);
    bg.strokeWidth(preset.border?.width ?? 0);
    bg.cornerRadius(preset.radius ?? 0);
    bg.width(text.width());
    bg.height(text.height());
  }

  node.preset = presetKey;
}

function syncBgToText(node) {
  const text = node.textNode;
  const bg = node.children?.find?.((c) => c.getClassName?.() === 'Rect');
  if (text && bg) {
    bg.width(text.width());
    bg.height(text.height());
  }
}

function beginTextEdit(engine, group, { isPlacement }) {
  const presetKey = group.preset;
  const preset = PRESETS[presetKey] ?? PRESETS.standard;
  const text = group.textNode;
  const color = preset.textColor === 'currentColor'
    ? state.get('selectedColor')
    : preset.textColor;

  // Hide the Konva node while the HTML editor is up — the editor renders the
  // same visual treatment, and keeping the canvas node visible would double-up.
  engine.deselect();
  const wasVisible = group.visible();
  group.visible(false);
  engine.contentLayer.batchDraw();

  const container = engine.stage.container();
  const editor = document.createElement('div');
  editor.contentEditable = 'true';
  editor.setAttribute('spellcheck', 'false');
  editor.dataset.waylightTextEditor = 'true';
  editor.textContent = isPlacement ? '' : (text.text() === ' ' ? '' : text.text());

  const fontSize = text.fontSize();
  const padding = preset.padding ?? 0;
  const radius = preset.radius ?? 0;
  const borderW = preset.border?.width ?? 0;
  const bgFill = resolveBg(preset, state.get('selectedColor'));

  const groupAbs = group.getAbsolutePosition();
  Object.assign(editor.style, {
    position: 'absolute',
    left: `${groupAbs.x - borderW}px`,
    top: `${groupAbs.y - borderW}px`,
    margin: '0',
    padding: `${padding}px`,
    fontFamily: preset.font,
    fontSize: `${fontSize}px`,
    fontWeight: String(preset.weight ?? 500),
    lineHeight: '1',
    color,
    background: bgFill || 'transparent',
    border: preset.border ? `${borderW}px solid ${state.get('selectedColor')}` : 'none',
    borderRadius: `${radius}px`,
    outline: 'none',
    whiteSpace: 'pre',
    minWidth: `${fontSize * 0.6}px`,
    minHeight: `${fontSize}px`,
    cursor: 'text',
    zIndex: '999',
    caretColor: color,
  });

  container.appendChild(editor);
  // Focus + place caret at end on next tick so layout has settled.
  requestAnimationFrame(() => {
    editor.focus();
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    const sel = document.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  });

  // A pointerdown anywhere outside the editor commits. We only swallow events
  // that land on the canvas itself — otherwise the active text tool would fire
  // again and place a second placeholder. Toolbar / sidebar clicks fall
  // through so the user doesn't have to click twice to switch tools.
  const ownerDoc = container.ownerDocument || document;
  const onOutsidePointer = (e) => {
    if (editor.contains(e.target)) return;
    if (container.contains(e.target)) {
      e.preventDefault();
      e.stopPropagation();
    }
    commit();
  };
  ownerDoc.addEventListener('pointerdown', onOutsidePointer, true);

  let committed = false;
  const commit = () => {
    if (committed) return;
    committed = true;
    const value = (editor.textContent ?? '').replace(/ /g, ' ');
    editor.removeEventListener('blur', commit);
    editor.removeEventListener('keydown', onKey);
    ownerDoc.removeEventListener('pointerdown', onOutsidePointer, true);
    editor.remove();

    if (!value.trim()) {
      // Empty commit: drop the node entirely. For placements this means no
      // ghost annotation and no undo entry; for re-edits we restore the
      // previous text rather than nuking an existing annotation.
      if (isPlacement) {
        group.destroy();
        engine.contentLayer.batchDraw();
        return;
      }
      group.visible(wasVisible);
      engine.contentLayer.batchDraw();
      engine.selectNode(group);
      return;
    }

    text.text(value);
    const bg = group.children?.find?.((c) => c.getClassName?.() === 'Rect');
    if (bg) {
      bg.width(text.width());
      bg.height(text.height());
    }
    group.visible(wasVisible);
    engine.contentLayer.batchDraw();
    if (isPlacement) commitInteractive(engine, group);
    engine.selectNode(group);
  };

  const onKey = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      // Enter commits; Shift+Enter inserts a newline.
      e.preventDefault();
      editor.blur();
    }
  };
  editor.addEventListener('blur', commit);
  editor.addEventListener('keydown', onKey);
}

export { PRESETS as TEXT_PRESETS };
